import { collection, query, where, limit, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * Check if an entity has related records that would be orphaned by deletion.
 * Uses limit(1) queries for performance — only checks existence.
 *
 * @param {string} companyId
 * @param {'customer'|'vendor'|'bank'|'inventoryItem'|'account'|'employee'|'job'|'region'|'area'|'location'} entityType
 * @param {string} entityId
 * @param {object} [entityData] - The entity object (needed for bank.linkedAccountId)
 * @returns {Promise<{hasReferences: boolean, message: string}>}
 */
export async function checkEntityReferences(companyId, entityType, entityId, entityData) {
  try {
    let refQuery;
    let label;

    switch (entityType) {
      case 'customer':
        refQuery = query(
          collection(db, 'companies', companyId, 'transactions'),
          where('partyId', '==', entityId),
          where('partyType', '==', 'customer'),
          limit(1)
        );
        label = 'transactions';
        break;

      case 'vendor':
        refQuery = query(
          collection(db, 'companies', companyId, 'transactions'),
          where('partyId', '==', entityId),
          where('partyType', '==', 'vendor'),
          limit(1)
        );
        label = 'transactions';
        break;

      case 'bank':
        refQuery = query(
          collection(db, 'companies', companyId, 'subTransactions'),
          where('accountId', '==', entityData?.linkedAccountId),
          limit(1)
        );
        label = 'sub-transactions';
        break;

      case 'staff':
        // Salary Staff has its own linked Level-5 account; check for any sub-transactions
        // posted against it (advance, loan, payroll, F&F entries).
        refQuery = query(
          collection(db, 'companies', companyId, 'subTransactions'),
          where('accountId', '==', entityData?.linkedAccountId),
          limit(1)
        );
        label = 'salary entries';
        break;

      case 'fixedAsset':
        // A fixed asset is referenced by its disposal transaction (FAD).
        // Depreciation runs reference it inside a snapshot array (not queryable);
        // a depreciated/disposed asset should never be hard-deleted anyway.
        refQuery = query(
          collection(db, 'companies', companyId, 'transactions'),
          where('sourceAssetId', '==', entityId),
          limit(1)
        );
        label = 'disposal entries';
        break;

      case 'inventoryItem':
        refQuery = query(
          collection(db, 'companies', companyId, 'stockFlow'),
          where('itemId', '==', entityId),
          limit(1)
        );
        label = 'stock-flow entries';
        break;

      case 'account':
        refQuery = query(
          collection(db, 'companies', companyId, 'subTransactions'),
          where('accountId', '==', entityId),
          limit(1)
        );
        label = 'sub-transactions';
        break;

      case 'employee':
        refQuery = query(
          collection(db, 'companies', companyId, 'transactions'),
          where('salesPersonId', '==', entityId),
          limit(1)
        );
        label = 'transactions';
        break;

      case 'job':
        refQuery = query(
          collection(db, 'companies', companyId, 'transactions'),
          where('jobId', '==', entityId),
          limit(1)
        );
        label = 'transactions';
        break;

      case 'region':
        refQuery = query(
          collection(db, 'companies', companyId, 'areas'),
          where('regionId', '==', entityId),
          limit(1)
        );
        label = 'areas';
        break;

      case 'area':
        refQuery = query(
          collection(db, 'companies', companyId, 'customers'),
          where('areaId', '==', entityId),
          limit(1)
        );
        label = 'customers';
        break;

      case 'location':
        refQuery = query(
          collection(db, 'companies', companyId, 'stockFlow'),
          where('locationId', '==', entityId),
          limit(1)
        );
        label = 'stock-flow entries';
        break;

      default:
        return { hasReferences: false, message: '' };
    }

    const snapshot = await getDocs(refQuery);

    if (!snapshot.empty) {
      return {
        hasReferences: true,
        message: `Cannot delete: this ${entityType} has related ${label}. Please delete those records first.`,
      };
    }

    return { hasReferences: false, message: '' };
  } catch (error) {
    console.error('Error checking entity references:', error);
    // Block deletion on error as a safety measure
    return {
      hasReferences: true,
      message: 'Cannot delete: unable to verify whether related records exist. Please try again.',
    };
  }
}
